import { useEffect, useMemo, useRef } from "react";
import {
  Sofa, Sparkles, Gift, Users, Headphones, ChevronRight, Wrench, Lightbulb, Package,
} from "lucide-react";

import { useResource } from "../lib/useApi";
import { beacon } from "../lib/api";
import { Glass, Section, Skeleton } from "../ui";

/**
 * Offers.jsx — the advertising and affiliate surface.
 *
 * Three slots, deliberately different shapes so a client can tell them apart:
 *
 *   tile        small square add-ons the studio itself sells (services)
 *   curated     a wide promo card, one partner per card  (paid placement)
 *   might_like  a quiet image strip, discovery rather than sell
 *
 * WHAT THE SERVER SENDS AND WHAT IT DOES NOT
 * ------------------------------------------
 * /portal/offers returns display fields only. `is_affiliate`, `payout_note`
 * and every commercial term stay on the studio side of the API — a client can
 * see a partner offer, never what the studio earns from it. That boundary is
 * enforced in routes/portal_offers.php by an explicit output whitelist, not by
 * this file remembering not to render something.
 *
 * DISCLOSURE
 * ----------
 * A card from a partner carries the partner's name and a "Partner offer"
 * badge. That is not decoration and should not be removed: an unlabelled paid
 * placement inside a portal a client trusts for their own project data is the
 * fastest way to lose the trust the portal exists to build.
 *
 * MEASUREMENT
 * -----------
 * One impression per card per session, sent through beacon() so it can never
 * block a paint or raise an error at a client. Clicks are recorded on the way
 * out. IntersectionObserver rather than "it rendered", because a card three
 * screens down a scroll was never seen.
 */

const ICONS = {
  sofa: Sofa, sparkles: Sparkles, gift: Gift, users: Users,
  headphones: Headphones, wrench: Wrench, lightbulb: Lightbulb, package: Package,
};

export function Offers({ slot, title, ctx }) {
  const { pid } = ctx;
  const { data, loading } = useResource(
    `/portal/offers?slot=${encodeURIComponent(slot)}${pid ? `&project_id=${pid}` : ""}`
  );
  const items = Array.isArray(data?.offers) ? data.offers : [];

  if (loading) {
    return (
      <Section title={title}>
        <div className="h-scroll">
          <Skeleton h={slot === "curated" ? 150 : 120} w={slot === "curated" ? 300 : 132} r={20} />
          <Skeleton h={slot === "curated" ? 150 : 120} w={slot === "curated" ? 300 : 132} r={20} />
        </div>
      </Section>
    );
  }
  if (!items.length) return null;

  return (
    <Section title={title}>
      <div className="h-scroll">
        {items.map((o) =>
          slot === "curated" ? <Promo key={o.id} offer={o} ctx={ctx} />
            : slot === "might_like" ? <LikeCard key={o.id} offer={o} ctx={ctx} />
              : <Tile key={o.id} offer={o} ctx={ctx} />
        )}
      </div>
    </Section>
  );
}

/* ---- one card, three shapes --------------------------------------------- */

function Promo({ offer, ctx }) {
  const ref = useSeen(offer);
  return (
    <Glass
      as="button"
      className="promo"
      ref={ref}
      strong
      style={{ "--promo-glow": offer.accent ? hexGlow(offer.accent) : undefined }}
      onClick={() => act(offer, ctx)}
    >
      {offer.badge && <span className="promo-badge">{offer.badge}</span>}
      <h3>{offer.title}</h3>
      {offer.subtitle && <p>{offer.subtitle}</p>}
      <span className="promo-cta">{offer.cta_label || "Know more"}</span>
      {offer.partner && <span className="promo-partner">Partner offer · {offer.partner}</span>}
    </Glass>
  );
}

function Tile({ offer, ctx }) {
  const ref = useSeen(offer);
  const Icon = ICONS[offer.icon] || Package;
  return (
    <button className="tile neu" ref={ref} onClick={() => act(offer, ctx)}>
      {offer.image_url ? (
        <span className="tile-art" style={{ backgroundImage: `url(${offer.image_url})`, backgroundSize: "cover" }} />
      ) : (
        <span className="tile-art"><Icon size={26} strokeWidth={1.4} /></span>
      )}
      <b>{offer.title}</b>
      {offer.subtitle && <small>{offer.subtitle}</small>}
    </button>
  );
}

function LikeCard({ offer, ctx }) {
  const ref = useSeen(offer);
  return (
    <Glass as="button" className="like" ref={ref} onClick={() => act(offer, ctx)}>
      <span
        className="like-art"
        style={offer.image_url ? { backgroundImage: `url(${offer.image_url})` } : undefined}
      />
      <span className="like-body">
        <b>{offer.title}</b>
        {offer.subtitle && <small>{offer.subtitle}</small>}
        {offer.partner && <small style={{ color: "var(--mauve-deep)" }}>Partner · {offer.partner}</small>}
      </span>
    </Glass>
  );
}

/* =========================================================================
   QUICK TILES — the studio's own three, always present, never sold.
   Reference screen 4 puts "Call manager / Rewards / Refer & earn" here; the
   D'leaps equivalents are a designer callback, the review reward and the
   referral programme.
   ========================================================================= */

export function QuickTiles({ ctx }) {
  const { open } = ctx;
  const tiles = [
    ["Talk to your designer", "Usually replies in a few hours", Headphones, () => open("support")],
    ["Rewards", "Unlocked by your Care cover", Sparkles, () => open("care")],
    ["Refer & earn ₹25,000", "For every friend who signs", Gift, () => open("refer")],
  ];
  return (
    <Section>
      <div className="h-scroll">
        {tiles.map(([title, note, Icon, onClick]) => (
          <button className="tile glass" key={title} onClick={onClick}>
            <span className="tile-art"><Icon size={24} strokeWidth={1.4} /></span>
            <b>{title}</b>
            <small>{note}</small>
          </button>
        ))}
      </div>
    </Section>
  );
}

/* ---- behaviour ----------------------------------------------------------- */

const seen = new Set();

/** Records one impression the first time a card is at least half on screen. */
function useSeen(offer) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || seen.has(offer.id)) return;
    if (typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !seen.has(offer.id)) {
            seen.add(offer.id);
            beacon("/portal/offers/track", { offer_id: offer.id, event: "impression" });
            io.disconnect();
          }
        }
      },
      { threshold: 0.5 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [offer.id]);
  return ref;
}

/**
 * What a tap does. `cta_kind` keeps the routing on the server so a new offer
 * never needs a frontend release:
 *
 *   tab       open one of the portal's own sheets  (cta_target = sheet name)
 *   ticket    raise a support ticket pre-filled with the offer's subject
 *   whatsapp  open the studio's WhatsApp with the offer named
 *   link      an external URL, opened in a new tab with rel=noopener
 */
function act(offer, ctx) {
  beacon("/portal/offers/track", { offer_id: offer.id, event: "click" });
  const kind = offer.cta_kind || "link";

  if (kind === "tab" && offer.cta_target) return ctx.open(offer.cta_target);

  if (kind === "ticket") return ctx.open("support");

  if (kind === "whatsapp" && offer.cta_url) {
    window.open(offer.cta_url, "_blank", "noopener,noreferrer");
    return;
  }

  if (offer.cta_url) window.open(offer.cta_url, "_blank", "noopener,noreferrer");
}

/** A translucent wash of the offer's accent, for the blob behind a promo. */
function hexGlow(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex).trim());
  if (!m) return undefined;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, 0.35)`;
}
